import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, updateDoc, collection, addDoc, Timestamp, deleteDoc, query, where, getDocs, deleteField, onSnapshot } from 'firebase/firestore';
import { db, setMatchConfirmation } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import PotentialMatches from './PotentialMatches';
import ConfirmationDialog from './ConfirmationDialog';
import PackageSizeSelector from './PackageSizeSelector';
import { getCountryFlag, getCountryNameFromCode, countries as countryList } from '../utils/countryUtils';
import { DEFAULT_QUIKBAK_FEE } from '../utils/shippingUtils';
import { fetchShippingEstimate } from '../utils/shippingApi';
import PaymentSection from './PaymentSection';

// Item types with icons (same as ItemTypeDropdown)
const itemTypes = [
  { name: 'Phone', icon: '📱' },
  { name: 'Wallet', icon: '👛' },
  { name: 'Keys', icon: '🔑' },
  { name: 'Jewelry', icon: '💍' },
  { name: 'Watch', icon: '⌚' },
  { name: 'Sunglasses', icon: '🕶️' },
  { name: 'Headphones', icon: '🎧' },
  { name: 'Charger', icon: '🔌' },
  { name: 'Laptop', icon: '💻' },
  { name: 'Tablet', icon: '📱' },
  { name: 'Camera', icon: '📷' },
  { name: 'Bag', icon: '👜' },
  { name: 'Backpack', icon: '🎒' },
  { name: 'Jacket', icon: '🧥' },
  { name: 'Shoes', icon: '👟' },
  { name: 'Hat', icon: '👒' },
  { name: 'Scarf', icon: '🧣' },
  { name: 'Umbrella', icon: '☂️' },
  { name: 'Book', icon: '📚' },
  { name: 'Documents', icon: '📄' },
  { name: 'Passport', icon: '📘' },
  { name: 'Credit Card', icon: '💳' },
  { name: 'Medication', icon: '💊' },
  { name: 'Toiletries', icon: '🧴' },
];

export default function LostItemDetails() {
  const { t } = useTranslation();
  const [item, setItem] = useState(null);
  const [matchedFoundItem, setMatchedFoundItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isStaffOrAdmin, setIsStaffOrAdmin] = useState(false);
  const [showUnmatchDialog, setShowUnmatchDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showPackageSizeSelector, setShowPackageSizeSelector] = useState(false);
  const [selectedPackageSize, setSelectedPackageSize] = useState(null);
  const { id, hotelId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [userClaims, setUserClaims] = useState(null);
  const [matchData, setMatchData] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editableItem, setEditableItem] = useState(null);
  const [paymentSuccessMessage, setPaymentSuccessMessage] = useState('');
  const [shippingQuote, setShippingQuote] = useState(null);
  const [shippingQuoteLoading, setShippingQuoteLoading] = useState(false);
  const [shippingQuoteError, setShippingQuoteError] = useState('');
  const [showFullReport, setShowFullReport] = useState(false);
  const [hasConfirmedMatch, setHasConfirmedMatch] = useState(false);
  const [updatingConfirmation, setUpdatingConfirmation] = useState(false);
  const [showMatchedFoundDetails, setShowMatchedFoundDetails] = useState(false);

  useEffect(() => {
    async function checkUserRole() {
      if (currentUser) {
        const idTokenResult = await currentUser.getIdTokenResult();
        const isAdmin = idTokenResult.claims.admin === true;
        const isStaff = idTokenResult.claims.staff === true;
        setIsStaffOrAdmin(isAdmin || isStaff);
        setUserClaims(idTokenResult.claims);
      } else {
        setIsStaffOrAdmin(false);
        setUserClaims(null);
      }
    }
    checkUserRole();
  }, [currentUser]);

useEffect(() => {
  if (!id || isStaffOrAdmin === null) {
    return undefined;
  }

  setLoading(true);
  setError('');

  const lostItemRef = doc(db, 'lost-items', id);
  let unsubscribeMatch = null;
  let unsubscribeFoundItem = null;
  let currentMatchId = '';
  let currentFoundId = '';

  const unsubscribeLostItem = onSnapshot(lostItemRef, async (lostSnap) => {
    if (!lostSnap.exists()) {
      setItem(null);
      setMatchData(null);
      setMatchedFoundItem(null);
      setLoading(false);
      setError(t('lostItemDetails.messages.itemNotFound'));
      if (unsubscribeMatch) {
        unsubscribeMatch();
        unsubscribeMatch = null;
      }
      if (unsubscribeFoundItem) {
        unsubscribeFoundItem();
        unsubscribeFoundItem = null;
      }
      return;
    }

    const lostData = { id: lostSnap.id, ...lostSnap.data() };
    setItem(lostData);

    if (isStaffOrAdmin && lostData.viewed === false) {
      try {
        await updateDoc(lostItemRef, { viewed: true });
      } catch (viewError) {
        console.warn('Failed to mark lost item as viewed:', viewError);
      }
    }

    const nextMatchId = typeof lostData.matchId === 'string' ? lostData.matchId : '';

    if (nextMatchId !== currentMatchId) {
      if (unsubscribeMatch) {
        unsubscribeMatch();
        unsubscribeMatch = null;
      }
      if (unsubscribeFoundItem) {
        unsubscribeFoundItem();
        unsubscribeFoundItem = null;
      }

      currentMatchId = nextMatchId;

      if (nextMatchId) {
        const matchRef = doc(db, 'matches', nextMatchId);
        unsubscribeMatch = onSnapshot(matchRef, async (matchSnap) => {
          if (!matchSnap.exists()) {
            setMatchData(null);
            setMatchedFoundItem(null);
            return;
          }

          const matchPayload = { id: matchSnap.id, ...matchSnap.data() };
          setMatchData(matchPayload);
          setSelectedPackageSize(matchPayload.packageSize || null);
          const paymentComplete = matchPayload.paymentStatus === 'succeeded' || matchPayload.status === 'ready-to-ship' || matchPayload.status === 'scheduled-for-pickup' || matchPayload.status === 'shipped';
          setShowPackageSizeSelector(!paymentComplete && !matchPayload.packageSize && isStaffOrAdmin);

          const nextFoundId = typeof matchPayload.foundItemId === 'string' ? matchPayload.foundItemId : '';
          if (nextFoundId !== currentFoundId) {
            if (unsubscribeFoundItem) {
              unsubscribeFoundItem();
              unsubscribeFoundItem = null;
            }

            currentFoundId = nextFoundId;
            if (nextFoundId) {
              const foundRef = doc(db, 'found-items', nextFoundId);
              unsubscribeFoundItem = onSnapshot(foundRef, (foundSnap) => {
                if (foundSnap.exists()) {
                  setMatchedFoundItem({ id: foundSnap.id, ...foundSnap.data() });
                } else {
                  setMatchedFoundItem(null);
                }
              }, (foundError) => {
                console.error('Failed to listen to found item:', foundError);
              });
            } else {
              setMatchedFoundItem(null);
            }
          }
        }, (matchError) => {
          console.error('Failed to listen to match data:', matchError);
          setError(t('lostItemDetails.messages.errorFetching') + matchError.message);
        });
      } else {
        setMatchData(null);
        setMatchedFoundItem(null);
        currentFoundId = '';
      }
    }

    setLoading(false);
  }, (lostError) => {
    console.error('Failed to listen to lost item:', lostError);
    setError(t('lostItemDetails.messages.errorFetching') + lostError.message);
    setLoading(false);
  });

  return () => {
    unsubscribeLostItem();
    if (unsubscribeMatch) {
      unsubscribeMatch();
    }
    if (unsubscribeFoundItem) {
      unsubscribeFoundItem();
    }
  };
}, [id, isStaffOrAdmin, t]);

  useEffect(() => {
    if (item) {
      setEditableItem({ ...item });
    }
  }, [item]);

  useEffect(() => {
    if (matchData?.status === 'waiting-for-payment') {
      setPaymentSuccessMessage('');
    }
  }, [matchData?.status]);

  useEffect(() => {
    if (!isStaffOrAdmin && matchData?.paymentStatus === 'succeeded') {
      // Update message based on current match status
      if (matchData.status === 'shipped') {
        setPaymentSuccessMessage(t('payment.messages.shipped'));
      } else if (matchData.status === 'scheduled-for-pickup') {
        setPaymentSuccessMessage(t('payment.messages.scheduledForPickup'));
      } else if (matchData.status === 'ready-to-ship') {
        setPaymentSuccessMessage(t('payment.messages.alreadyPaid'));
      }
    }
  }, [isStaffOrAdmin, matchData?.paymentStatus, matchData?.status, t]);

  const handleEdit = () => {
    setEditableItem({ ...item });
    setEditMode(true);
  };

  const handleCancel = () => {
    setEditableItem({ ...item });
    setEditMode(false);
  };

  const handleSave = async () => {
    setLoading(true);
    const dataToUpdate = { ...editableItem };
    
    // Convert date strings to Timestamps
    if (dataToUpdate.dateLost && typeof dataToUpdate.dateLost === 'string') {
        dataToUpdate.dateLost = Timestamp.fromDate(new Date(dataToUpdate.dateLost));
    }
    if (dataToUpdate.stayDateFrom && typeof dataToUpdate.stayDateFrom === 'string') {
        dataToUpdate.stayDateFrom = Timestamp.fromDate(new Date(dataToUpdate.stayDateFrom));
    }
    if (dataToUpdate.stayDateUntil && typeof dataToUpdate.stayDateUntil === 'string') {
        dataToUpdate.stayDateUntil = Timestamp.fromDate(new Date(dataToUpdate.stayDateUntil));
    }

    // remove fields that should not be updated
    delete dataToUpdate.id;
    delete dataToUpdate.createdAt;
    
    dataToUpdate.updatedAt = Timestamp.now();

    try {
        const itemRef = doc(db, "lost-items", id);
        await updateDoc(itemRef, dataToUpdate);
        setEditMode(false);
    } catch (err) {
        setError(t('lostItemDetails.messages.errorUpdating') + err.message);
        console.error(err);
    } finally {
        setLoading(false);
    }
  };

  const handleInputChange = (e) => {
      const { name, value } = e.target;
      setEditableItem(prev => ({ ...prev, [name]: value }));
  };

  const handleMatch = async (foundItem) => {
    try {
      const matchesRef = collection(db, "matches");
      const now = Timestamp.now();
      const matchDoc = await addDoc(matchesRef, {
        lostItemId: id,
        foundItemId: foundItem.id,
        hotelId: hotelId,
        status: 'matched',
        matchedAt: now,
        createdAt: now,
        updatedAt: now,
        customerConfirmed: false
      });

      const lostItemRef = doc(db, "lost-items", id);
      await updateDoc(lostItemRef, {
        matchId: matchDoc.id,
        status: 'matched'
      });

      const foundItemRef = doc(db, "found-items", foundItem.id);
      await updateDoc(foundItemRef, {
        matchId: matchDoc.id,
        status: 'matched'
      });

      setItem(prev => ({ ...prev, matchId: matchDoc.id, status: 'matched' }));
      setMatchedFoundItem(foundItem);
      setMatchData({
        id: matchDoc.id,
        lostItemId: id,
        foundItemId: foundItem.id,
        status: 'matched',
        matchedAt: now,
        createdAt: now,
        updatedAt: now,
        customerConfirmed: false
      });

      // Redirect to the newly created match page
      navigate(`/${hotelId}/matches/${matchDoc.id}`);
    } catch (err) {
      setError(t('lostItemDetails.messages.errorMatching') + err.message);
    }
  };

  const handleUnmatch = async () => {
    if (!isStaffOrAdmin || !canUnmatch) {
      setShowUnmatchDialog(false);
      return;
    }

    try {
      if (item.matchId) {
        const matchRef = doc(db, "matches", item.matchId);
        await deleteDoc(matchRef);
      }

      const lostItemRef = doc(db, "lost-items", id);
      await updateDoc(lostItemRef, {
        matchId: null,
        status: 'pending'
      });

      if (matchData?.foundItemId) {
        const foundItemRef = doc(db, "found-items", matchData.foundItemId);
        await updateDoc(foundItemRef, {
          matchId: null,
          status: 'pending'
        });
      }

      setItem(prev => ({
        ...prev,
        matchId: null,
        status: 'pending'
      }));
      setMatchedFoundItem(null);
      setMatchData(null);
      setShowUnmatchDialog(false);
    } catch (err) {
      setError(t('lostItemDetails.messages.errorUnmatching') + err.message);
    }
  };

  const handlePackageSizeSelect = async (size, weight) => {
    if (!canEditPackage) {
      return;
    }
    try {
      const country = item.country;
      if (!country) {
        console.error("No country specified in lost item");
        return;
      }

      const now = Timestamp.now();
      const isFinalised = matchData?.status === 'ready-to-ship' || matchData?.status === 'scheduled-for-pickup' || matchData?.status === 'shipped';
      
      const matchRef = doc(db, "matches", item.matchId);
      const baseUpdate = {
        packageSize: size,
        updatedAt: now,
      };

      const firestoreUpdate = { ...baseUpdate };

      if (weight && weight.trim() !== '') {
        const parsedWeight = parseFloat(weight);
        if (!Number.isNaN(parsedWeight)) {
          baseUpdate.packageWeight = parsedWeight;
          firestoreUpdate.packageWeight = parsedWeight;
        }
      }

      if (!isFinalised) {
        firestoreUpdate.status = 'waiting-for-payment';
        firestoreUpdate.paymentTotal = deleteField();
        firestoreUpdate.destinationCountry = deleteField();
        firestoreUpdate.destinationCountryCode = deleteField();
        firestoreUpdate.selectedShippingCountryName = deleteField();
        firestoreUpdate.selectedShippingCountryCode = deleteField();
        firestoreUpdate.shippingAddress = deleteField();
        firestoreUpdate.readyToShipCode = deleteField();
      }

      await updateDoc(matchRef, firestoreUpdate);

      if (!isFinalised) {
        const updates = { status: 'waiting-for-payment', updatedAt: now };
        try {
          const lostItemRef = doc(db, "lost-items", id);
          await updateDoc(lostItemRef, updates);
          setItem(prev => ({
            ...prev,
            ...updates
          }));
        } catch (lostItemUpdateError) {
          console.warn('Failed to update lost item status to waiting-for-payment:', lostItemUpdateError);
        }

        if (matchData?.foundItemId) {
          const foundItemRef = doc(db, "found-items", matchData.foundItemId);
          updateDoc(foundItemRef, {
            status: 'waiting-for-payment',
            updatedAt: now
          }).catch(foundItemUpdateError => {
            console.warn('Failed to update found item status to waiting-for-payment:', foundItemUpdateError);
          });
        }
      }

      setSelectedPackageSize(size);
      setShippingQuote(null);
      setShippingQuoteError('');
      setShowPackageSizeSelector(false);
    } catch (error) {
      console.error("Error updating package size:", error);
    }
  };

  const handlePaymentComplete = (result) => {
    const payload = result?.data || result || {};
    const paymentStatus = payload.paymentStatus || 'succeeded';
    const matchStatus = payload.matchStatus || 'ready-to-ship';
    const shippingAddress = payload.shippingAddress || matchData?.shippingAddress || null;
    const totalAmount = typeof payload.totalAmount === 'number'
      ? payload.totalAmount
      : matchData?.paymentTotal || payload.paymentAmount || null;
    const destinationCountry = payload.destinationCountry || shippingAddress?.country || matchData?.destinationCountry || null;
    const destinationCountryCode = payload.destinationCountryCode || shippingAddress?.countryCode || matchData?.destinationCountryCode || null;
    const readyToShipCode = typeof payload.readyToShipCode === 'string' && payload.readyToShipCode.trim() !== ''
      ? payload.readyToShipCode.trim()
      : matchData?.readyToShipCode || null;

    if (
      typeof payload.shippingCost === 'number' ||
      typeof payload.quikbakFee === 'number' ||
      typeof payload.totalAmount === 'number'
    ) {
      setShippingQuote({
        shippingCost: typeof payload.shippingCost === 'number' ? payload.shippingCost : null,
        quikbakFee: typeof payload.quikbakFee === 'number' ? payload.quikbakFee : null,
        totalAmount: typeof payload.totalAmount === 'number' ? payload.totalAmount : totalAmount,
        currency: (payload.currency || matchData?.paymentCurrency || 'jpy').toLowerCase(),
        countryName: destinationCountry || '',
        countryCode: destinationCountryCode || '',
      });
    }

    setMatchData(prev => prev ? ({
      ...prev,
      status: matchStatus,
      paymentStatus,
      shippingAddress,
      paymentTotal: totalAmount,
      destinationCountry,
      destinationCountryCode,
      paymentCurrency: payload.currency || prev.paymentCurrency,
      readyToShipCode,
      customerConfirmed: true,
    }) : prev);

    setItem(prev => prev ? ({
      ...prev,
      status: matchStatus
    }) : prev);

    setMatchedFoundItem(prev => prev ? ({
      ...prev,
      status: matchStatus
    }) : prev);

    if (!isStaffOrAdmin) {
      const messageKey = payload?.alreadySucceeded ? 'payment.messages.alreadyPaid' : 'payment.messages.success';
      setPaymentSuccessMessage(t(messageKey));
    }
  };

  const handleMatchEstimateUpdate = useCallback((updates) => {
    setMatchData(prev => prev ? ({
      ...prev,
      ...updates
    }) : prev);
    setShippingQuote(null);
    setShippingQuoteError('');
  }, []);

  const handleConfirmationToggle = async (event) => {
    const nextValue = event.target.checked;
    const previousValue = hasConfirmedMatch;

    setHasConfirmedMatch(nextValue);
    setMatchData(prev => prev ? ({
      ...prev,
      customerConfirmed: nextValue
    }) : prev);

    if (!matchData?.id) {
      return;
    }

    setUpdatingConfirmation(true);
    setError('');

    try {
      if (isStaffOrAdmin) {
        const matchRef = doc(db, 'matches', matchData.id);
        const now = Timestamp.now();
        await updateDoc(matchRef, {
          customerConfirmed: nextValue,
          customerConfirmedAt: nextValue ? now : deleteField(),
          updatedAt: now,
        });
      } else {
        await setMatchConfirmation({
          matchId: matchData.id,
          lostItemId: matchData?.lostItemId || item?.id || id,
          confirmed: nextValue,
        });
      }
    } catch (confirmError) {
      console.error('Failed to update match confirmation state:', confirmError);
      setError(t('lostItemDetails.messages.errorUpdating') + (confirmError?.message || ''));
      setHasConfirmedMatch(previousValue);
      setMatchData(prev => prev ? ({
        ...prev,
        customerConfirmed: previousValue
      }) : prev);
    } finally {
      setUpdatingConfirmation(false);
    }
  };

  const fallbackCountryName = item?.country || null;
  const fallbackCountryCode = fallbackCountryName
    ? (countryList.find((c) => c.name === fallbackCountryName)?.isoCode || '').toUpperCase()
    : '';

  const isPaymentFinalised = matchData?.paymentStatus === 'succeeded' || matchData?.status === 'ready-to-ship' || matchData?.status === 'scheduled-for-pickup' || matchData?.status === 'shipped';
  const canUnmatch = Boolean(isStaffOrAdmin && matchData && !isPaymentFinalised);
  const canEditPackage = Boolean(isStaffOrAdmin && matchData && !isPaymentFinalised);

  useEffect(() => {
    if (!canUnmatch && showUnmatchDialog) {
      setShowUnmatchDialog(false);
    }
  }, [canUnmatch, showUnmatchDialog]);

  useEffect(() => {
    if (isStaffOrAdmin && matchData && !matchData.packageSize && !isPaymentFinalised) {
      setShowPackageSizeSelector(true);
    }
  }, [isStaffOrAdmin, matchData?.packageSize, isPaymentFinalised]);

  useEffect(() => {
    if (!canEditPackage && showPackageSizeSelector) {
      setShowPackageSizeSelector(false);
    }
  }, [canEditPackage, showPackageSizeSelector]);

  useEffect(() => {
    setShowMatchedFoundDetails(false);
  }, [matchData?.id]);

  useEffect(() => {
    if (!matchData) {
      setHasConfirmedMatch(false);
      return;
    }

    if (isPaymentFinalised) {
      setHasConfirmedMatch(true);
      return;
    }

    // Don't override local state while an update is in progress
    if (updatingConfirmation) {
      return;
    }

    setHasConfirmedMatch(Boolean(matchData.customerConfirmed));
  }, [matchData?.id, matchData?.customerConfirmed, isPaymentFinalised, updatingConfirmation]);

  useEffect(() => {
    if (!isStaffOrAdmin) {
      setShippingQuote(null);
      setShippingQuoteError('');
      setShippingQuoteLoading(false);
      return;
    }

    if (!matchData?.id || !matchData?.packageSize) {
      setShippingQuote(null);
      return;
    }

    if (matchData?.paymentStatus === 'succeeded') {
      return;
    }

    const preferredCountryName =
      matchData?.selectedShippingCountryName ||
      matchData?.destinationCountry ||
      matchData?.shippingAddress?.country ||
      fallbackCountryName ||
      null;

    if (!preferredCountryName) {
      setShippingQuote(null);
      return;
    }

    const preferredCountryCode =
      matchData?.selectedShippingCountryCode ||
      matchData?.destinationCountryCode ||
      matchData?.shippingAddress?.countryCode ||
      fallbackCountryCode ||
      '';

    let ignore = false;
    setShippingQuoteLoading(true);
    setShippingQuoteError('');

    fetchShippingEstimate({
      matchId: matchData.id,
      countryName: preferredCountryName,
      countryCode: preferredCountryCode,
      currency: matchData?.paymentCurrency || 'jpy',
    })
      .then((quote) => {
        if (!ignore) {
          setShippingQuote(quote);
        }
      })
      .catch((error) => {
        if (!ignore) {
          console.error('Failed to fetch shipping estimate:', error);
          setShippingQuote(null);
          setShippingQuoteError(error?.message || t('payment.messages.error'));
        }
      })
      .finally(() => {
        if (!ignore) {
          setShippingQuoteLoading(false);
        }
      });

    return () => {
      ignore = true;
    };
  }, [fallbackCountryCode, fallbackCountryName, isStaffOrAdmin, matchData?.destinationCountry, matchData?.destinationCountryCode, matchData?.id, matchData?.packageSize, matchData?.paymentCurrency, matchData?.paymentStatus, matchData?.selectedShippingCountryCode, matchData?.selectedShippingCountryName, matchData?.shippingAddress?.country, matchData?.shippingAddress?.countryCode, t]);
  const shippingCostValue = shippingQuote?.shippingCost ?? null;

  const quikbakFeeValue = shippingQuote?.quikbakFee ?? DEFAULT_QUIKBAK_FEE;

  const totalChargeValue = typeof matchData?.paymentTotal === 'number'
    ? matchData.paymentTotal
    : (shippingQuote?.totalAmount ?? null);

  const shippingCountryCode = shippingQuote?.countryCode
    || matchData?.shippingAddress?.countryCode
    || matchData?.destinationCountryCode
    || matchData?.selectedShippingCountryCode
    || fallbackCountryCode;

  const shippingCountryName = shippingQuote?.countryName
    || getCountryNameFromCode(shippingCountryCode)
    || matchData?.shippingAddress?.country
    || matchData?.destinationCountry
    || matchData?.selectedShippingCountryName
    || fallbackCountryName
    || t('lostItemDetails.values.unknown');
  const shouldShowFullReport = isStaffOrAdmin || showFullReport;
  const primaryImage = Array.isArray(item?.imageUrls) && item.imageUrls.length > 0 ? item.imageUrls[0] : null;
  const matchReadyForPayment =
    matchData?.status === 'waiting-for-payment' && matchData?.paymentStatus !== 'succeeded';
  const shouldShowAnonConfirmation =
    matchData && !isStaffOrAdmin && matchData.paymentStatus !== 'succeeded';

  const handleDelete = async () => {
    setShowDeleteDialog(false);
    setLoading(true);
    try {
      if (item.matchId) {
        const matchRef = doc(db, 'matches', item.matchId);
        await deleteDoc(matchRef);
        console.log('Associated match deleted:', item.matchId);

        const foundItemQuery = query(collection(db, 'found-items'), where('matchId', '==', item.matchId));
        const foundItemSnapshot = await getDocs(foundItemQuery);
        foundItemSnapshot.forEach(async (foundDoc) => {
           await updateDoc(foundDoc.ref, { matchId: null, status: 'pending' });
        });
      }

      await deleteDoc(doc(db, 'lost-items', id));
      navigate(`/${hotelId}/admin`);
    } catch (err) {
      setError(t('lostItemDetails.messages.errorDeleting'));
      console.error(err);
      setLoading(false);
    }
  };

  const handleMarkAsUnread = async () => {
    if (!isStaffOrAdmin) return;

    try {
      const lostItemRef = doc(db, "lost-items", id);
      await updateDoc(lostItemRef, { viewed: false });
      
      // Update local state
      setItem(prev => ({ ...prev, viewed: false }));
      console.log('Marked lost item as unread:', id);
    } catch (err) {
      setError(t('lostItemDetails.messages.errorMarkingUnread') + err.message);
      console.error(err);
    }
  };

  const formatDate = (date) => {
    if (!date) return t('adminDashboard.table.noData');
    
    try {
      if (date instanceof Timestamp) {
        return date.toDate().toLocaleDateString();
      }
      if (date instanceof Date) {
        return date.toLocaleDateString();
      }
      return new Date(date).toLocaleDateString();
    } catch (e) {
      console.error('Error formatting date:', e);
      return t('lostItemDetails.messages.invalidDate');
    }
  };

  const toInputDate = (date) => {
    if (!date) return '';
    let d;
    if (date instanceof Timestamp) {
      d = date.toDate();
    } else {
      d = new Date(date);
    }
    if (isNaN(d.valueOf())) return '';
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Helper function to get item type icon
  const getItemTypeIcon = (itemType) => {
    const item = itemTypes.find(it => it.name === itemType);
    return item ? item.icon : '❓';
  };

  // Helper function to get localized item type name
  const getLocalizedItemType = (itemType) => {
    if (!itemType) return t('adminDashboard.table.noData');
    return t(`itemTypes.${itemType.toLowerCase().replace(' ', '_')}`, { defaultValue: itemType });
  };

  if (loading) {
    return <div className="loading-container">{t('lostItemDetails.loading')}</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className="item-details-container">
      <div className="item-details-header">
        {currentUser && (
          <button onClick={() => navigate(`/${hotelId}/admin`)} className="back-button">
            &larr; {item?.status === 'matched' ? 
              t('lostItemDetails.doneButton') : 
              t('lostItemDetails.backButton')}
          </button>
        )}
        <h2>{t('lostItemDetails.title')}</h2>
        {isStaffOrAdmin && (
          <div className="admin-actions">
            {editMode ? (
              <>
                <button onClick={handleSave} className="save-button" disabled={loading}>
                  {t('lostItemDetails.buttons.saveChanges')}
                </button>
                <button onClick={handleCancel} className="cancel-button" disabled={loading}>
                  {t('lostItemDetails.buttons.cancel')}
                </button>
              </>
            ) : (
              <>
                {item && item.viewed !== false && (
                  <button onClick={handleMarkAsUnread} disabled={loading} className="mark-unread-button">
                    {t('lostItemDetails.buttons.markAsUnread')}
                  </button>
                )}
                <button onClick={handleEdit} className="edit-button" disabled={loading}>
                  {t('lostItemDetails.buttons.editItem')}
                </button>
                <button onClick={() => setShowDeleteDialog(true)} disabled={loading} className="delete-button">
                  {t('lostItemDetails.buttons.deleteItem')}
                </button>
              </>
            )}
          </div>
        )}
      </div>
      
      {item && (
        <>
          {item.deleted && (
            <div className="deleted-banner">
              {t('lostItemDetails.messages.itemDeleted')}
            </div>
          )}

          {!isStaffOrAdmin && (
            <div className="anon-summary-card">
              <div className="summary-header">
                <h3>{t(matchData ? 'lostItemDetails.anon.summaryWithMatchTitle' : 'lostItemDetails.anon.summaryTitle')}</h3>
                <p>{t(matchData ? 'lostItemDetails.anon.summaryWithMatchMessage' : 'lostItemDetails.anon.summaryMessage')}</p>
              </div>
              <div className="summary-body">
                {primaryImage ? (
                  <img src={primaryImage} alt={t('lostItemDetails.anon.summaryImageAlt')} className="summary-image" />
                ) : (
                  <div className="summary-placeholder">{t('lostItemDetails.anon.summaryNoImage')}</div>
                )}
                <div className="summary-meta">
                  <div>{t('lostItemDetails.fields.dateLost')}: {formatDate(item.dateLost)}</div>
                  <div>{t('lostItemDetails.fields.locationLost')}: {item.locationLost || t('lostItemDetails.values.unknown')}</div>
                </div>
              </div>
              <button
                type="button"
                className="summary-toggle-button"
                onClick={() => setShowFullReport(prev => !prev)}
              >
                {showFullReport ? t('lostItemDetails.anon.hideFullReport') : t('lostItemDetails.anon.viewFullReport')}
              </button>
            </div>
          )}
          <div className="item-details-content">
            {shouldShowFullReport && (
            <>
            <div className="detail-section">
              <h3>{t('lostItemDetails.sections.itemInformation')}</h3>
              <div className="detail-grid">
                <div className="detail-item">
                  <div className="detail-label">{t('lostItemDetails.fields.itemType')}</div>
                  {editMode ? (
                    <select name="itemType" value={editableItem.itemType || ''} onChange={handleInputChange} className="detail-input">
                      {itemTypes.map(type => <option key={type.name} value={type.name}>{type.icon} {getLocalizedItemType(type.name)}</option>)}
                    </select>
                  ) : (
                    <div className="detail-value">
                      <span className="item-type-icon" style={{ marginRight: '8px' }}>
                        {getItemTypeIcon(item.itemType)}
                      </span>
                      {getLocalizedItemType(item.itemType)}
                    </div>
                  )}
                </div>
                <div className="detail-item">
                  <div className="detail-label">{t('lostItemDetails.fields.itemName')}</div>
                  {editMode ? (
                    <input type="text" name="itemName" value={editableItem.itemName || ''} onChange={handleInputChange} className="detail-input" />
                  ) : (
                    <div className="detail-value">{item.itemName || t('adminDashboard.table.noData')}</div>
                  )}
                </div>
                <div className="detail-item">
                  <div className="detail-label">{t('lostItemDetails.fields.dateLost')}</div>
                  {editMode ? (
                    <input type="date" name="dateLost" value={toInputDate(editableItem.dateLost)} onChange={handleInputChange} className="detail-input" />
                  ) : (
                    <div className="detail-value">{formatDate(item.dateLost)}</div>
                  )}
                </div>
                <div className="detail-item">
                  <div className="detail-label">{t('lostItemDetails.fields.locationLost')}</div>
                  {editMode ? (
                    <input type="text" name="locationLost" value={editableItem.locationLost || ''} onChange={handleInputChange} className="detail-input" />
                  ) : (
                    <div className="detail-value">{item.locationLost || t('lostItemDetails.values.unknown')}</div>
                  )}
                </div>
                <div className="detail-item full-width">
                  <div className="detail-label">{t('lostItemDetails.fields.description')}</div>
                  {editMode ? (
                    <textarea name="description" value={editableItem.description || ''} onChange={handleInputChange} className="detail-textarea" />
                  ) : (
                    <div className="detail-value comment-text">{item.description || t('lostItemDetails.values.noDescription')}</div>
                  )}
                </div>
                {item.imageUrls && item.imageUrls.length > 0 && (
                  <div className="detail-item full-width">
                    <div className="detail-label">{t('lostItemDetails.fields.photos')}</div>
                    <div className="image-preview-container">
                      {item.imageUrls.map((url, index) => (
                        <div key={index} className="image-preview">
                          <img src={url} alt={`Item photo ${index + 1}`} />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            <div className="detail-section">
              <h3>{t('lostItemDetails.sections.contactInformation')}</h3>
              <div className="detail-grid">
                <div className="detail-item">
                  <div className="detail-label">{t('lostItemDetails.fields.firstName')}</div>
                  {editMode ? (
                    <input type="text" name="firstName" value={editableItem.firstName || ''} onChange={handleInputChange} className="detail-input" />
                  ) : (
                    <div className="detail-value">{item.firstName || t('adminDashboard.table.noData')}</div>
                  )}
                </div>
                <div className="detail-item">
                  <div className="detail-label">{t('lostItemDetails.fields.surname')}</div>
                  {editMode ? (
                    <input type="text" name="surname" value={editableItem.surname || ''} onChange={handleInputChange} className="detail-input" />
                  ) : (
                    <div className="detail-value">{item.surname || t('adminDashboard.table.noData')}</div>
                  )}
                </div>
                <div className="detail-item">
                  <div className="detail-label">{t('lostItemDetails.fields.phone')}</div>
                  {editMode ? (
                    <input type="text" name="phone" value={editableItem.phone || ''} onChange={handleInputChange} className="detail-input" />
                  ) : (
                    <div className="detail-value">{item.phone || t('adminDashboard.table.noData')}</div>
                  )}
                </div>
                <div className="detail-item">
                  <div className="detail-label">{t('lostItemDetails.fields.email')}</div>
                  {editMode ? (
                    <input type="email" name="email" value={editableItem.email || ''} onChange={handleInputChange} className="detail-input" />
                  ) : (
                    <div className="detail-value">{item.email || t('adminDashboard.table.noData')}</div>
                  )}
                </div>
                <div className="detail-item">
                  <div className="detail-label">{t('lostItemDetails.fields.country')}</div>
                  {editMode ? (
                    <select name="country" value={editableItem.country || ''} onChange={handleInputChange} className="detail-input">
                      <option value="">{t('lostItemDetails.values.selectCountry')}</option>
                      {countryList.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
                    </select>
                  ) : (
                    <div className="detail-value">
                      {item.country && (
                        <span className="country-flag" style={{ marginRight: '8px' }}>
                          {getCountryFlag(item.country)}
                        </span>
                      )}
                      {item.country || t('adminDashboard.table.noData')}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="detail-section">
              <h3>{t('lostItemDetails.sections.stayInformation')}</h3>
              <div className="detail-grid">
                <div className="detail-item">
                  <div className="detail-label">{t('lostItemDetails.fields.stayDateFrom')}</div>
                  {editMode ? (
                    <input type="date" name="stayDateFrom" value={toInputDate(editableItem.stayDateFrom)} onChange={handleInputChange} className="detail-input" />
                  ) : (
                    <div className="detail-value">{formatDate(item.stayDateFrom) || t('adminDashboard.table.noData')}</div>
                  )}
                </div>
                <div className="detail-item">
                  <div className="detail-label">{t('lostItemDetails.fields.stayDateUntil')}</div>
                  {editMode ? (
                    <input type="date" name="stayDateUntil" value={toInputDate(editableItem.stayDateUntil)} onChange={handleInputChange} className="detail-input" />
                  ) : (
                    <div className="detail-value">{formatDate(item.stayDateUntil) || t('adminDashboard.table.noData')}</div>
                  )}
                </div>
              </div>
            </div>

            <div className="detail-section">
              <h3>{t('lostItemDetails.sections.systemInformation')}</h3>
              <div className="detail-grid">
                <div className="detail-item">
                  <div className="detail-label">{t('lostItemDetails.fields.status')}</div>
                  <div className="detail-value status-badge">{item.status || t('lostItemDetails.values.pending')}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-label">{t('lostItemDetails.fields.createdAt')}</div>
                  <div className="detail-value">
                    {item.createdAt ? new Date(item.createdAt.toDate()).toLocaleString() : t('adminDashboard.table.noData')}
                  </div>
                </div>
                {item.viewed === false && isStaffOrAdmin && (
                  <div className="detail-item">
                    <div className="detail-label">{t('lostItemDetails.fields.notification')}</div>
                    <div className="detail-value">
                      <span className="new-item-badge">{t('lostItemDetails.values.new')}</span> {t('lostItemDetails.messages.newItemViewed')}
                    </div>
                  </div>
                )}
                {item.matchId && (
                  <div className="detail-item">
                    <div className="detail-label">{t('lostItemDetails.fields.matchDetails')}</div>
                    <div className="detail-value">
                      <span 
                        className="link-text"
                        onClick={() => navigate(`/${hotelId}/matches/${item.matchId}`)}
                        style={{ 
                          cursor: 'pointer',
                          color: '#2196f3',
                          textDecoration: 'underline'
                        }}
                      >
                        {t('lostItemDetails.messages.viewMatchDetails')}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
            </>
            )}

            {isStaffOrAdmin === true && item && !item.matchId && hotelId && (
              <PotentialMatches item={item} itemType="lost" onMatch={handleMatch} hotelId={hotelId} />
            )}

            {matchedFoundItem && (
              <div className="detail-section matched-item-section">
                <div className="section-header">
                  <h3 
                    onClick={() => isStaffOrAdmin ? navigate(`/${hotelId}/found-items/${matchedFoundItem.id}`) : null}
                    style={{ 
                      cursor: isStaffOrAdmin ? 'pointer' : 'default',
                      color: isStaffOrAdmin ? '#2196f3' : 'inherit',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px'
                    }}
                  >
                    {t('lostItemDetails.sections.matchedFoundItem')}
                    {isStaffOrAdmin && <span style={{ fontSize: '0.8em' }}>→</span>}
                  </h3>
                  {canUnmatch && (
                    <button 
                      className="unmatch-button"
                      onClick={() => setShowUnmatchDialog(true)}
                    >
                      {t('lostItemDetails.buttons.unmatchItems')}
                    </button>
                  )}
                </div>

                {!isStaffOrAdmin && (
                  <div className="anon-summary-card">
                    <div className="summary-header">
                      <h4>{t('lostItemDetails.anon.foundSummaryTitle')}</h4>
                      <p>{t('lostItemDetails.anon.foundSummaryMessage')}</p>
                    </div>
                    <div className="summary-body">
                      {matchedFoundItem.imageUrls && matchedFoundItem.imageUrls.length > 0 ? (
                        <img
                          src={matchedFoundItem.imageUrls[0]}
                          alt={t('lostItemDetails.alt.matchedItemPhoto', { index: 1 })}
                          className="summary-image"
                        />
                      ) : (
                        <div className="summary-placeholder">{t('lostItemDetails.values.noData')}</div>
                      )}
                      <div className="summary-meta">
                        <div>{t('lostItemDetails.fields.itemType')}: {matchedFoundItem.itemType || t('adminDashboard.table.noData')}</div>
                        <div>{t('lostItemDetails.fields.itemName')}: {matchedFoundItem.itemName || t('adminDashboard.table.noData')}</div>
                        <div>{t('lostItemDetails.fields.dateFound')}: {formatDate(matchedFoundItem.dateFound)}</div>
                        <div>{t('lostItemDetails.fields.locationFound')}: {matchedFoundItem.locationFound || t('lostItemDetails.values.unknown')}</div>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="summary-toggle-button"
                      onClick={() => setShowMatchedFoundDetails(prev => !prev)}
                    >
                      {showMatchedFoundDetails ? t('lostItemDetails.anon.foundHideDetails') : t('lostItemDetails.anon.foundViewDetails')}
                    </button>
                  </div>
                )}

                {(isStaffOrAdmin || showMatchedFoundDetails) && (
                  <div className="detail-grid">
                    <div className="detail-item">
                      <div className="detail-label">{t('lostItemDetails.fields.itemType')}</div>
                      <div className="detail-value">
                        <span className="item-type-icon" style={{ marginRight: '8px' }}>
                          {getItemTypeIcon(matchedFoundItem.itemType)}
                        </span>
                        {getLocalizedItemType(matchedFoundItem.itemType)}
                      </div>
                    </div>
                    <div className="detail-item">
                      <div className="detail-label">{t('lostItemDetails.fields.itemName')}</div>
                      <div className="detail-value">{matchedFoundItem.itemName || t('adminDashboard.table.noData')}</div>
                    </div>
                    <div className="detail-item">
                      <div className="detail-label">{t('lostItemDetails.fields.dateFound')}</div>
                      <div className="detail-value">{formatDate(matchedFoundItem.dateFound)}</div>
                    </div>
                    <div className="detail-item">
                      <div className="detail-label">{t('lostItemDetails.fields.locationFound')}</div>
                      <div className="detail-value">{matchedFoundItem.locationFound || t('lostItemDetails.values.unknown')}</div>
                    </div>
                    <div className="detail-item">
                      <div className="detail-label">{t('lostItemDetails.fields.storageLocation')}</div>
                      <div className="detail-value">{matchedFoundItem.storageLocation || t('lostItemDetails.values.unknown')}</div>
                    </div>
                    <div className="detail-item full-width">
                      <div className="detail-label">{t('lostItemDetails.fields.finderName')}</div>
                      <div className="detail-value">{matchedFoundItem.finderName || t('adminDashboard.table.noData')}</div>
                    </div>
                    <div className="detail-item full-width">
                      <div className="detail-label">{t('lostItemDetails.fields.comments')}</div>
                      <div className="detail-value comment-text">{matchedFoundItem.comments || t('lostItemDetails.values.noComments')}</div>
                    </div>
                    {matchedFoundItem.imageUrls && matchedFoundItem.imageUrls.length > 0 && (
                      <div className="detail-item full-width">
                        <div className="detail-label">{t('lostItemDetails.fields.photos')}</div>
                        <div className="image-preview-container">
                          {matchedFoundItem.imageUrls.map((url, index) => (
                            <div key={index} className="image-preview">
                              <img src={url} alt={t('lostItemDetails.alt.matchedItemPhoto', { index: index + 1 })} />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

              </div>
            )}


            {matchData?.shippingAddress && matchData?.paymentStatus === 'succeeded' && (
              <div className="detail-section" style={{ marginTop: '16px' }}>
                <h3>{t('lostItemDetails.fields.shippingAddress')}</h3>
                <div className="detail-grid">
                  <div className="detail-item full-width">
                    <div className="detail-value" style={{ lineHeight: '1.6' }}>
                      <div>{matchData.shippingAddress.name}</div>
                      <div>{matchData.shippingAddress.line1}</div>
                      {matchData.shippingAddress.line2 && matchData.shippingAddress.line2.trim() !== '' && (
                        <div>{matchData.shippingAddress.line2}</div>
                      )}
                      <div>
                        {[matchData.shippingAddress.city, matchData.shippingAddress.state]
                          .filter(Boolean)
                          .join(', ')}{matchData.shippingAddress.postalCode ? ` ${matchData.shippingAddress.postalCode}` : ''}
                      </div>
                      <div>
                        <span style={{ marginRight: '6px' }}>
                          {getCountryFlag(matchData.shippingAddress.countryCode || matchData.shippingAddress.country)}
                        </span>
                        {getCountryNameFromCode(matchData.shippingAddress.countryCode) || matchData.shippingAddress.country}
                      </div>
                      {matchData.shippingAddress.phone && matchData.shippingAddress.phone.trim() !== '' && (
                        <div>{matchData.shippingAddress.phone}</div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Shipping Status and Tracking - Separate section with better spacing */}
                {(matchData.status === 'scheduled-for-pickup' || matchData.status === 'shipped') && (
                  <div style={{ marginTop: '24px', paddingTop: '20px', borderTop: '1px solid #e0e0e0' }}>
                    <h4 style={{ marginBottom: '16px', fontSize: '1rem', fontWeight: '600' }}>
                      {t('lostItemDetails.sections.shippingStatus')}
                    </h4>
                    <div className="detail-grid">
                      <div className="detail-item">
                        <div className="detail-label">{t('lostItemDetails.fields.status')}</div>
                        <div className="detail-value">
                          {matchData.status === 'scheduled-for-pickup'
                            ? t('lostItemDetails.values.waitingForCollection')
                            : t('lostItemDetails.values.shipped')}
                        </div>
                      </div>
                      {matchData.shippingTrackingNumber && (
                        <div className="detail-item">
                          <div className="detail-label">{t('lostItemDetails.fields.trackingNumber')}</div>
                          <div className="detail-value" style={{ fontWeight: '600' }}>{matchData.shippingTrackingNumber}</div>
                        </div>
                      )}
                      {matchData.scheduledPickupDate && matchData.status === 'scheduled-for-pickup' && (
                        <div className="detail-item">
                          <div className="detail-label">{t('lostItemDetails.fields.scheduledPickupDate')}</div>
                          <div className="detail-value">{matchData.scheduledPickupDate}</div>
                        </div>
                      )}
                      {matchData.shippedAt && matchData.status === 'shipped' && (
                        <div className="detail-item">
                          <div className="detail-label">{t('lostItemDetails.fields.shippedDate')}</div>
                          <div className="detail-value">{formatDate(matchData.shippedAt)}</div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {shouldShowAnonConfirmation && (
              <div className="confirmation-card" style={{ marginTop: '20px' }}>
                <h3>{t('lostItemDetails.anon.confirmMatchTitle')}</h3>
                <p>{matchReadyForPayment
                  ? t('lostItemDetails.anon.confirmMatchMessage')
                  : t('lostItemDetails.anon.preparingPackageMessage')}</p>
                <label className="confirmation-checkbox">
                  <input
                    type="checkbox"
                    checked={hasConfirmedMatch}
                    onChange={handleConfirmationToggle}
                    disabled={updatingConfirmation || isPaymentFinalised}
                  />
                  <span>{t('lostItemDetails.anon.confirmMatchLabel')}</span>
                </label>
                {!matchReadyForPayment && hasConfirmedMatch && (
                  <div className="prep-note">{t('lostItemDetails.anon.preparingPackageNote')}</div>
                )}
              </div>
            )}

            {/* Manual Return Status - Show after confirmation */}
            {matchData?.status === 'manual-return' && !isStaffOrAdmin && hasConfirmedMatch && (
              <div className="detail-section" style={{ marginTop: '16px' }}>
                <h3>{t('lostItemDetails.sections.returnStatus')}</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <div className="detail-label">{t('lostItemDetails.fields.returnMethod')}</div>
                    <div className="detail-value">
                      {matchData.returnType === 'in-person'
                        ? t('matchDetails.returnType.inPerson')
                        : t('matchDetails.returnType.group')}
                    </div>
                  </div>
                  {matchData.returnDetails && (
                    <div className="detail-item full-width">
                      <div className="detail-label">{t('lostItemDetails.fields.returnDetails')}</div>
                      <div className="detail-value" style={{ whiteSpace: 'pre-wrap' }}>
                        {matchData.returnDetails}
                      </div>
                    </div>
                  )}
                  {matchData.manualReturnAt && (
                    <div className="detail-item">
                      <div className="detail-label">{t('lostItemDetails.fields.returnedDate')}</div>
                      <div className="detail-value">{formatDate(matchData.manualReturnAt)}</div>
                    </div>
                  )}
                </div>
                <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#e8f5e9', borderRadius: '4px' }}>
                  <p style={{ margin: 0, color: '#2e7d32' }}>
                    {t('lostItemDetails.messages.manualReturnComplete')}
                  </p>
                </div>
              </div>
            )}

            {/* Disposed Status - Show after confirmation */}
            {matchData?.status === 'disposed' && !isStaffOrAdmin && hasConfirmedMatch && (
              <div className="detail-section" style={{ marginTop: '16px' }}>
                <h3>{t('lostItemDetails.sections.disposedStatus')}</h3>
                <div className="detail-grid">
                  {matchData.disposedAt && (
                    <div className="detail-item">
                      <div className="detail-label">{t('lostItemDetails.fields.disposedDate')}</div>
                      <div className="detail-value">{formatDate(matchData.disposedAt)}</div>
                    </div>
                  )}
                </div>
                <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#fff3e0', borderRadius: '4px', border: '1px solid #ffb74d' }}>
                  <p style={{ margin: 0, color: '#e65100' }}>
                    {t('lostItemDetails.messages.itemDisposed')}
                  </p>
                </div>
              </div>
            )}

            {matchReadyForPayment && !isStaffOrAdmin && hasConfirmedMatch && (
              <div className="detail-section" style={{ marginTop: '20px' }}>
                <PaymentSection
                  matchId={matchData.id}
                  matchData={matchData}
                  defaultCountry={item?.country || ''}
                  onPaymentComplete={handlePaymentComplete}
                  onMatchUpdate={handleMatchEstimateUpdate}
                />
              </div>
            )}

            {paymentSuccessMessage && (
              <div className="success-message" role="status" style={{ marginTop: '16px' }}>
                {paymentSuccessMessage}
              </div>
            )}
          </div>
        </>
      )}

      <ConfirmationDialog
        isOpen={showUnmatchDialog}
        onClose={() => setShowUnmatchDialog(false)}
        onConfirm={handleUnmatch}
        title={t('lostItemDetails.confirmations.unmatchTitle')}
        message={t('lostItemDetails.confirmations.unmatchMessage')}
      />

      <ConfirmationDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDelete}
        title={t('lostItemDetails.confirmations.deleteTitle')}
        message={t('lostItemDetails.confirmations.deleteMessage')}
      />
    </div>
  );
} 
